import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, NativeSelect, DemoBadge } from "@/components/common";
import { ROLE_LABEL, fmtD } from "@/lib/format";

const blank = { email: "", name: "", role: "operatore", password: "", phone: "", commesse: [], mansioni: "", qualifiche: [], can_assign: false, active: true };

const UserDialog = ({ open, onOpenChange, initial, commesse, onSaved, me }) => {
  const [f, setF] = useState(blank);
  useEffect(() => {
    if (open) setF(initial ? { ...initial, password: "", mansioni: initial.mansioni.join(", "), qualifiche: initial.qualifiche.map((q) => ({ name: q.name, expires_at: q.expires_at ? q.expires_at.slice(0, 10) : "" })) } : blank);
  }, [open, initial]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    const body = { email: f.email, name: f.name, role: f.role, phone: f.phone || "", commesse: f.commesse, squadra_id: f.squadra_id || null,
      mansioni: f.mansioni.split(",").map((s) => s.trim()).filter(Boolean), can_assign: f.can_assign, active: f.active,
      qualifiche: f.qualifiche.filter((q) => q.name).map((q) => ({ name: q.name, expires_at: q.expires_at ? new Date(q.expires_at).toISOString() : null })),
      password: f.password || null };
    try {
      initial ? await api.put(`/users/${initial.id}`, body) : await api.post("/users", body);
      toast.success("Utente salvato");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const roles = Object.entries(ROLE_LABEL).filter(([k]) => k !== "ceo" || me.role === "ceo");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto rounded-2xl" data-testid="user-dialog">
        <DialogHeader><DialogTitle className="text-xl font-extrabold">{initial ? "Modifica utente" : "Nuovo utente"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1"><Label>Nome e cognome *</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="user-form-name" /></div>
          <div className="space-y-1"><Label>Email *</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="user-form-email" /></div>
          <div className="space-y-1"><Label>Ruolo *</Label><NativeSelect className="w-full" testid="user-form-role" value={f.role} onChange={(v) => set("role", v)}>{roles.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</NativeSelect></div>
          <div className="space-y-1"><Label>{initial ? "Nuova password (facoltativa)" : "Password iniziale *"}</Label><Input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="min. 8 caratteri" data-testid="user-form-password" /></div>
          <div className="space-y-1 sm:col-span-2"><Label>Commesse</Label>
            <div className="flex flex-wrap gap-2">{commesse.map((c) => (
              <label key={c.id} className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm"><input type="checkbox" checked={f.commesse.includes(c.id)} data-testid={`user-form-commessa-${c.code}`}
                onChange={(e) => set("commesse", e.target.checked ? [...f.commesse, c.id] : f.commesse.filter((x) => x !== c.id))} />{c.code}</label>
            ))}</div></div>
          <div className="space-y-1 sm:col-span-2"><Label>Mansioni (separate da virgola)</Label><Input value={f.mansioni} onChange={(e) => set("mansioni", e.target.value)} data-testid="user-form-mansioni" /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Qualifiche e abilitazioni</Label>
            {f.qualifiche.map((q, i) => (
              <div key={i} className="flex gap-2">
                <Input value={q.name} placeholder="Abilitazione" onChange={(e) => set("qualifiche", f.qualifiche.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                <Input type="date" className="w-44" value={q.expires_at} onChange={(e) => set("qualifiche", f.qualifiche.map((x, j) => (j === i ? { ...x, expires_at: e.target.value } : x)))} />
                <Button variant="ghost" size="icon" onClick={() => set("qualifiche", f.qualifiche.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="rounded-xl" onClick={() => set("qualifiche", [...f.qualifiche, { name: "", expires_at: "" }])} data-testid="user-form-add-qualifica"><Plus className="mr-1 h-3 w-3" />Aggiungi abilitazione</Button>
          </div>
          <label className="flex items-center justify-between rounded-xl border p-3 text-sm">Può distribuire incarichi<Switch checked={f.can_assign} onCheckedChange={(v) => set("can_assign", v)} /></label>
          <label className="flex items-center justify-between rounded-xl border p-3 text-sm">Account attivo<Switch checked={f.active} onCheckedChange={(v) => set("active", v)} data-testid="user-form-active" /></label>
        </div>
        <DialogFooter><Button onClick={save} className="rounded-xl font-bold" data-testid="user-form-submit">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Personale() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [edit, setEdit] = useState(undefined);
  const [role, setRole] = useState("");
  const [commessaId, setCommessaId] = useState("");
  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries({ role, commessa_id: commessaId }).filter(([, v]) => v));
    api.get("/users", { params }).then((r) => setUsers(r.data));
  }, [role, commessaId]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    api.get("/commesse").then((r) => setCommesse(r.data));
  }, []);
  const canEdit = ["ceo", "amministrazione"].includes(user.role);
  const codes = Object.fromEntries(commesse.map((c) => [c.id, c.code]));
  return (
    <div data-testid="personale-page">
      <PageHeader title="Personale" subtitle={`${users.length} persone`} actions={
        <>
          <NativeSelect testid="personale-filter-role" value={role} onChange={setRole}><option value="">Tutti i ruoli</option>{Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</NativeSelect>
          <NativeSelect testid="personale-filter-commessa" value={commessaId} onChange={setCommessaId}><option value="">Tutte le commesse</option>{commesse.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}</NativeSelect>
          {canEdit && <Button onClick={() => setEdit(null)} className="h-10 rounded-xl font-bold" data-testid="personale-new-button"><Plus className="mr-1 h-4 w-4" />Nuovo utente</Button>}
        </>} />
      <div className="card-soft overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm" data-testid="personale-table">
          <thead className="bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="px-4 py-3">Nome</th><th>Ruolo</th><th>Commesse</th><th>Abilitazioni</th><th>Stato</th><th></th></tr></thead>
          <tbody>{users.map((u) => (
            <tr key={u.id} className="border-t" data-testid={`personale-row-${u.email}`}>
              <td className="px-4 py-3"><p className="font-semibold">{u.name} <DemoBadge show={u.is_demo} /></p><p className="text-xs text-muted-foreground">{u.email}</p></td>
              <td>{ROLE_LABEL[u.role]}{u.can_assign && u.role === "caposquadra" && <span className="ml-1 text-xs text-primary">(assegna)</span>}</td>
              <td>{u.commesse.map((c) => codes[c]).filter(Boolean).join(", ") || "—"}</td>
              <td>{u.qualifiche.map((q) => { const exp = q.expires_at && new Date(q.expires_at) < new Date(); return <span key={q.name} className={`mr-1 inline-block rounded-full px-2 py-0.5 text-xs ${exp ? "bg-red-100 text-red-700" : "bg-accent"}`}>{q.name}{q.expires_at && ` · ${exp ? "scaduta" : "fino al"} ${fmtD(q.expires_at)}`}</span>; })}</td>
              <td>{u.active ? <span className="text-emerald-700">Attivo</span> : <span className="text-red-600">Disattivato</span>}</td>
              <td className="pr-4 text-right">{canEdit && <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setEdit(u)} data-testid={`personale-edit-${u.email}`}>Modifica</Button>}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      {canEdit && <UserDialog open={edit !== undefined} onOpenChange={(o) => !o && setEdit(undefined)} initial={edit} commesse={commesse} onSaved={load} me={user} />}
    </div>
  );
}
